import { ClipboardList } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  NotificationCard,
  PanelHeader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  StatusTabs,
} from '@/components';
import { constants, useTranslateNotification } from '@/lib';

export const NotificationModal = ({
  open,
  onOpenChange,
  notifications,
  onMarkAllAsRead,
  onNotificationClick,
  children,
}) => {
  const { t } = useTranslation();
  const translateNotification = useTranslateNotification();
  const [activeTab, setActiveTab] = useState(constants.UPDATES_PANEL.ALL_KEY);

  const tabCounts = useMemo(
    () => ({
      [constants.UPDATES_PANEL.ALL_KEY]: notifications.length,
      [constants.UPDATES_PANEL.UNREAD_KEY]: notifications.filter(n => !n.isRead).length,
    }),
    [notifications]
  );

  const tabs = useMemo(
    () => [
      { key: constants.UPDATES_PANEL.ALL_KEY, label: t('UPDATES_PANEL.ALL') },
      { key: constants.UPDATES_PANEL.UNREAD_KEY, label: t('UPDATES_PANEL.UNREAD') },
    ],
    [t]
  );

  const unreadNotifications = useMemo(() => notifications.filter(n => !n.isRead), [notifications]);

  const displayNotifications = activeTab === constants.UPDATES_PANEL.ALL_KEY ? notifications : unreadNotifications;
  const hasUnread = unreadNotifications.length > 0;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 p-0 rounded-lg shadow-lg flex flex-col overflow-hidden"
        sideOffset={12}
      >
        <PanelHeader title={t('UPDATES_PANEL.UPDATES')} onClickChange={onOpenChange} showClose />

        {/* Tabs */}
        <div className="flex items-end justify-between p-3">
          <StatusTabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} dataCount={tabCounts} />
          <Button
            wrap="normal"
            variant="link"
            size="link"
            onClick={onMarkAllAsRead}
            disabled={!hasUnread}
            aria-label={t('UPDATES_PANEL.MARK_AS_READ')}
          >
            {t('UPDATES_PANEL.MARK_AS_READ')}
          </Button>
        </div>

        {/* Scrollable Notifications */}
        <div className="h-[31.25rem] overflow-y-auto">
          {displayNotifications.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-muted-foreground text-sm">
              {activeTab === constants.UPDATES_PANEL.ALL_KEY
                ? t('UPDATES_PANEL.NO_NOTIFICATIONS')
                : t('UPDATES_PANEL.NO_UNREAD_NOTIFICATIONS')}
            </div>
          ) : (
            displayNotifications.map(notification => {
              const { title, description } = translateNotification(notification);
              return (
                <NotificationCard
                  key={notification.id}
                  icon={<ClipboardList />}
                  title={title}
                  description={description}
                  time={notification.timestamp || notification.time}
                  read={notification.isRead}
                  onClick={() => onNotificationClick?.(notification)}
                />
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
